import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { collection, addDoc, query, where, getDocs } from "firebase/firestore";
import db from "@/lib/firebaseDb";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type FormValues = z.infer<typeof formSchema>;

export default function Newsletter() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);

    try {
      // Check if email already exists
      const q = query(collection(db, "newsletter_subscribers"), where("email", "==", data.email));
      const existing = await getDocs(q);

      if (!existing.empty) {
        toast({
          title: "Already Subscribed",
          description: "This email is already subscribed to our newsletter.",
        });
        form.reset();
        return;
      }

      // Save to Firebase
      await addDoc(collection(db, "newsletter_subscribers"), {
        email: data.email,
        subscribedAt: new Date().toISOString(),
        source: window.location.pathname,
      });

      toast({
        title: "Subscription Successful!",
        description: "Thank you for subscribing to our newsletter.",
      });

      form.reset();
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast({
        title: "Subscription Failed",
        description: "There was a problem with your subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 bg-[#E30A17]">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-poppins font-bold text-white mb-4">Stay Updated</h2>
          <p className="text-white text-opacity-90 mb-8">
            Subscribe to our newsletter for the latest travel tips, destination guides, and special offers for your Turkish adventure.
          </p>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col md:flex-row gap-3">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder="Your email address"
                        aria-label="Email address for newsletter"
                        className="py-3 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-white"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="text-white text-opacity-90" />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="bg-white text-[#E30A17] font-poppins font-semibold py-3 px-6 rounded-lg hover:bg-opacity-90 transition duration-200"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
          </Form>

          <p className="text-white text-opacity-70 text-sm mt-4">
            We respect your privacy and will never share your information.
          </p>
        </div>
      </div>
    </section>
  );
}
